import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ShieldLogo } from "./Logo";
import { Button } from "../ui/button";
import { MessageSquare, LogIn, Clock, Eye, Mic, Shield, Sparkles } from "lucide-react";

const stories = [
  { time: "6:00 AM", label: "Supervisor Apollo", icon: Eye, color: "text-lime",
    desc: "Chef uploads a quick walk-in photo during morning prep. Apollo flags produce that won't survive the day — caught before service starts.",
    tag: "THE ALL-SEEING" },
  { time: "8:00 AM", label: "Supervisor Athena", icon: Mic, color: "text-blue-400",
    desc: "Head chef records the new risotto procedure by voice. Athena converts it into a formatted SOP with cost analysis — ready for the team in minutes.",
    tag: "THE STRATEGIST" },
  { time: "7:45 PM", label: "Supervisor Echo", icon: Shield, color: "text-gold",
    desc: "A guest mentions a nut allergy on a call. Echo catches it, flags the kitchen, and prevents a life-threatening mistake.",
    tag: "THE MESSENGER" },
];

export const HeroSection = ({ onOpenChat }) => {
  const [activeStory, setActiveStory] = useState(0);
  const s = stories[activeStory];
  const Icon = s.icon;

  const handleTalkToArgus = () => {
    // Find and click the chat launcher if it exists
    const chatLauncher = document.querySelector('[data-testid="chat-launcher"]');
    if (chatLauncher) {
      chatLauncher.click();
    }
    if (onOpenChat) onOpenChat();
  };

  const handleTrialLogin = () => {
    window.location.href = "/trial/login";
  };

  return (
    <section data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden">
      {/* Background grid */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: `linear-gradient(rgba(0,255,65,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(0,255,65,0.3) 1px, transparent 1px)`,
        backgroundSize: '60px 60px'
      }} />

      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-12 py-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left: Headline */}
          <div>
            <motion.div className="flex items-center gap-3 mb-8"
              initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5 }}>
              <ShieldLogo size="md" />
              <span className="font-inter text-sm font-bold tracking-[0.3em] uppercase text-gold">Argus Xenia</span>
            </motion.div>

            <motion.h1 data-testid="hero-headline"
              className="font-inter text-5xl sm:text-6xl lg:text-7xl font-black text-white leading-[1.05] tracking-tight"
              initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.6 }}>
              Your Restaurant,{" "}
              <span className="text-gold gold-text-glow">On Autopilot.</span>
            </motion.h1>

            <motion.p data-testid="hero-subhead"
              className="mt-6 text-base md:text-lg text-slate-300 max-w-lg leading-relaxed"
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4, duration: 0.5 }}>
              The first consolidated AI OS that sees, hears, and trains your team to protect your guests and your margins.
            </motion.p>

            {/* New AI-First CTAs */}
            <motion.div className="mt-10 flex flex-wrap gap-4"
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6, duration: 0.5 }}>
              <Button data-testid="hero-cta-chat" onClick={handleTalkToArgus}
                className="bg-gold hover:bg-gold/90 text-indigo-700 font-inter font-bold text-base px-8 py-6 rounded-none tracking-wide group">
                <MessageSquare className="mr-2 w-5 h-5 group-hover:animate-bounce" />
                Talk to AI Argus
                <Sparkles className="ml-2 w-4 h-4" />
              </Button>
              <Button data-testid="hero-cta-login" onClick={handleTrialLogin} variant="outline"
                className="border-gold/30 text-gold hover:bg-gold/10 font-inter font-medium px-8 py-6 rounded-none">
                <LogIn className="mr-2 w-4 h-4" /> Login to Trial
              </Button>
            </motion.div>

            {/* Trust indicator */}
            <motion.p 
              className="mt-4 text-xs text-slate-500"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8 }}>
              <Sparkles className="inline w-3 h-3 mr-1 text-gold" />
              AI Argus will learn about your restaurant and show you personalized savings
            </motion.p>
          </div>

          {/* Right: Story Scroller */}
          <motion.div data-testid="story-scroller"
            className="relative" initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.5, duration: 0.6 }}>
            <div className="glass-card rounded-xl p-6 border border-lime/10">
              {/* Story tabs */}
              <div className="flex gap-2 mb-6">
                {stories.map((st, i) => (
                  <button key={i} data-testid={`story-tab-${i}`} onClick={() => setActiveStory(i)}
                    className={`flex-1 py-2 px-3 text-xs font-inter font-bold tracking-wider rounded-none border transition-colors duration-300 ${
                      activeStory === i
                        ? "bg-lime/10 border-lime/30 text-lime"
                        : "bg-indigo-500/20 border-indigo-400/20 text-slate-400 hover:text-white"
                    }`}>
                    {st.time}
                  </button>
                ))}
              </div>

              <AnimatePresence mode="wait">
                <motion.div key={activeStory}
                  initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -15 }}
                  transition={{ duration: 0.3 }}>
                  {/* Timestamp + AI ring */}
                  <div className="flex items-center gap-4 mb-4">
                    <div className="relative w-12 h-12 flex items-center justify-center">
                      <div className="absolute inset-0 rounded-full border-2 border-lime/40 animate-pulse-lime" />
                      <Icon className={`w-5 h-5 ${s.color}`} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <Clock className="w-3 h-3 text-slate-500" />
                        <span className="text-xs font-mono text-slate-400">{s.time}</span>
                      </div>
                      <h3 className="font-inter font-bold text-lg text-white">{s.label}</h3>
                    </div>
                  </div>

                  {/* Tag callout */}
                  <div className="bg-lime/5 border border-lime/20 rounded px-4 py-3 mb-4">
                    <span className="font-inter font-black text-sm text-lime tracking-wider">{s.tag}</span>
                  </div>

                  <p className="text-sm text-slate-300 leading-relaxed">{s.desc}</p>
                </motion.div>
              </AnimatePresence>
            </div>
          </motion.div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#040F49] to-transparent z-10" />
    </section>
  );
};
